import { apiClient, idempotentConfig, tenantHub, unwrap } from './client';
import type {
  ApiEnvelope,
  ProvisionSpokeRequest,
  ProvisionSpokeResult,
  QuotaScope,
  Spoke,
  SpokeList,
  TenantIdentityView,
  TenantProfile,
  TenantQuotaView,
  UpdateTenantProfileRequest,
  UpdateTenantQuotasRequest,
} from './types';

/**
 * B1 — tenant admin (F-TA-03/04/05). Spoke inventory + self-serve provisioning, regime/compliance
 * profile, and capability allowlist / per-family quotas / flow-cost budget. NEW cross-track endpoints
 * (Track A builds them in parallel); the SPA degrades to a teaching empty state where they 404.
 * `{hub}` is the caller's own hub; mutations carry `ol-idempotency-key`.
 */

const enc = (s: string) => encodeURIComponent(s);
const base = () => `/v1/tenants/${enc(tenantHub())}`;

/** GET /v1/tenants/{hub}/spokes — the hub's spokes. `{spokes:[…], totalCount}` — never a bare array. */
export async function listSpokes(): Promise<SpokeList> {
  const { data } = await apiClient.get<ApiEnvelope<SpokeList>>(`${base()}/spokes`);
  const body = unwrap(data);
  return { spokes: Array.isArray(body?.spokes) ? body.spokes : [], totalCount: body?.totalCount ?? 0 };
}

/** POST /v1/tenants/{hub}/spokes — provision a spoke. 503 PROVISIONING_UNAVAILABLE handled by caller. */
export async function provisionSpoke(
  body: ProvisionSpokeRequest,
  idemKey?: string,
): Promise<ProvisionSpokeResult> {
  const { data } = await apiClient.post<ApiEnvelope<ProvisionSpokeResult>>(
    `${base()}/spokes`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** GET /v1/tenants/{hub}/profile — regime / compliance profile. */
export async function getTenantProfile(): Promise<TenantProfile> {
  const { data } = await apiClient.get<ApiEnvelope<TenantProfile>>(`${base()}/profile`);
  return unwrap(data) ?? {};
}

/** PUT /v1/tenants/{hub}/profile. */
export async function updateTenantProfile(
  body: UpdateTenantProfileRequest,
  idemKey?: string,
): Promise<TenantProfile> {
  const { data } = await apiClient.put<ApiEnvelope<TenantProfile>>(
    `${base()}/profile`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data) ?? {};
}

/** Defensive normalisation of one quota scope (never crash on a partial payload). */
function normalizeScope(raw: Partial<QuotaScope> | undefined, fallbackScope: string): QuotaScope {
  return {
    scope: raw?.scope ?? fallbackScope,
    allowlist: Array.isArray(raw?.allowlist) ? raw.allowlist : [],
    quotas: raw?.quotas && typeof raw.quotas === 'object' ? raw.quotas : {},
    flowCostBudgetMicrosUsd:
      typeof raw?.flowCostBudgetMicrosUsd === 'number' ? raw.flowCostBudgetMicrosUsd : null,
    updatedAt: raw?.updatedAt ?? null,
  };
}

function normalizeQuotaView(body: Partial<TenantQuotaView> | undefined): TenantQuotaView {
  return {
    hub: body?.hub ?? tenantHub(),
    defaults: normalizeScope(body?.defaults, '_hub_'),
    spokeOverrides: Array.isArray(body?.spokeOverrides)
      ? body.spokeOverrides.map((s) => normalizeScope(s, ''))
      : [],
  };
}

/**
 * GET /v1/tenants/{hub}/quotas — the NESTED (hub, spoke) quota surface: `defaults` (the hub scope)
 * plus `spokeOverrides` (one scope per overriding spoke). Never a flat allowlist/quotas body.
 */
export async function getTenantQuotas(): Promise<TenantQuotaView> {
  const { data } = await apiClient.get<ApiEnvelope<TenantQuotaView>>(`${base()}/quotas`);
  return normalizeQuotaView(unwrap(data));
}

/** PUT /v1/tenants/{hub}/quotas — flat request body; the response echoes the full nested view. */
export async function updateTenantQuotas(
  body: UpdateTenantQuotasRequest,
  idemKey?: string,
): Promise<TenantQuotaView> {
  const { data } = await apiClient.put<ApiEnvelope<TenantQuotaView>>(
    `${base()}/quotas`,
    body,
    idempotentConfig(idemKey),
  );
  return normalizeQuotaView(unwrap(data));
}

/**
 * GET /v1/tenants/self/identity — the chrome chip's server-owned facts (display name + variant),
 * served to EVERY authenticated hub principal (literal `self`, not the `{hub}` path). Cached for
 * the life of the tab: the hub's identity cannot change under a live session, and the chip must
 * never turn one page load into N identity calls. A failure caches as null (the chip falls back
 * to the sign-in slug) and is retried on the next full reload — never a render-loop of retries.
 */
let identityCache: Promise<TenantIdentityView | null> | null = null;

export function getTenantIdentity(): Promise<TenantIdentityView | null> {
  if (!identityCache) {
    identityCache = apiClient
      .get<ApiEnvelope<TenantIdentityView>>('/v1/tenants/self/identity')
      .then(({ data }) => {
        const body = unwrap(data);
        return body && typeof body.hubId === 'string' && typeof body.displayName === 'string'
          ? body
          : null;
      })
      .catch(() => null);
  }
  return identityCache;
}

/** Test seam: drop the memoised identity (module-level caches outlive vitest cases). */
export function resetTenantIdentityCache(): void {
  identityCache = null;
}

export type { Spoke };
